import { supabase, DatabaseMosquePhoto } from '../lib/supabase';
import { MosquePhoto, PhotoUploadData } from '../types/mosque';
import { PhotoUploadError } from '../utils/photoUploadErrors';

const PHOTO_BUCKET = 'mosque-photos';
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

const toMosquePhoto = (photo: DatabaseMosquePhoto, url: string): MosquePhoto => ({
  id: photo.id,
  mosqueId: photo.mosque_id,
  url,
  storagePath: photo.storage_path,
  fileName: photo.file_name,
  fileSize: photo.file_size,
  mimeType: photo.mime_type,
  caption: photo.caption,
  displayOrder: photo.display_order,
  status: photo.status,
  createdAt: new Date(photo.created_at),
});

const buildStoragePath = (mosqueId: string, fileName: string): string => {
  const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : '';
  const safeExtension = extension && extension.length <= 6 ? `.${extension}` : '';
  return `pending/${mosqueId}/${Date.now()}-${crypto.randomUUID()}${safeExtension}`;
};

const validateFile = (file: File) => {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    throw new PhotoUploadError('PHOTO_INVALID_TYPE', `Unsupported file type: ${file.type}`);
  }

  if (file.size > MAX_PHOTO_SIZE_BYTES) {
    throw new PhotoUploadError('PHOTO_TOO_LARGE', `File "${file.name}" is larger than 5MB`);
  }
};

const createSignedPhotoUrl = async (storagePath: string): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(PHOTO_BUCKET)
    .createSignedUrl(storagePath, 60 * 60);

  if (error || !data?.signedUrl) {
    throw error || new Error('Failed to create signed URL');
  }

  return data.signedUrl;
};

const isNetworkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message.toLowerCase() : String(error || '').toLowerCase();
  return message.includes('failed to fetch') || message.includes('network');
};

export const usePhotos = () => {
  const moderatePhotoFallback = async (
    photoId: string,
    status: 'approved' | 'rejected',
    rejectionReason: string | null
  ) => {
    const { error } = await supabase
      .from('mosque_photos')
      .update({
        status,
        reviewed_at: new Date().toISOString(),
        rejection_reason: rejectionReason,
      })
      .eq('id', photoId);

    if (error) {
      throw error;
    }
  };

  const uploadPhoto = async (
    mosqueId: string,
    photo: PhotoUploadData,
    displayOrder: number
  ): Promise<void> => {
    validateFile(photo.file);

    const storagePath = buildStoragePath(mosqueId, photo.file.name);

    const { error: uploadError } = await supabase.storage
      .from(PHOTO_BUCKET)
      .upload(storagePath, photo.file, {
        contentType: photo.file.type,
        upsert: false,
      });

    if (uploadError) {
      if (isNetworkError(uploadError)) {
        throw new PhotoUploadError('PHOTO_UPLOAD_NETWORK_ERROR', uploadError.message);
      }
      throw new PhotoUploadError('PHOTO_UPLOAD_STORAGE_ERROR', uploadError.message);
    }

    const { error: insertError } = await supabase
      .from('mosque_photos')
      .insert({
        mosque_id: mosqueId,
        storage_path: storagePath,
        file_name: photo.file.name,
        file_size: photo.file.size,
        mime_type: photo.file.type,
        caption: photo.caption || null,
        display_order: displayOrder,
        status: 'pending',
      });

    if (insertError) {
      await supabase.storage.from(PHOTO_BUCKET).remove([storagePath]);
      if (isNetworkError(insertError)) {
        throw new PhotoUploadError('PHOTO_UPLOAD_NETWORK_ERROR', insertError.message);
      }
      throw new PhotoUploadError('PHOTO_UPLOAD_DATABASE_ERROR', insertError.message);
    }

    return;
  };

  const uploadPhotos = async (mosqueId: string, photos: PhotoUploadData[]) => {
    for (let index = 0; index < photos.length; index += 1) {
      await uploadPhoto(mosqueId, photos[index], index);
    }
  };

  const fetchApprovedPhotosForMosque = async (mosqueId: string): Promise<MosquePhoto[]> => {
    const { data, error } = await supabase
      .from('mosque_photos')
      .select('*')
      .eq('mosque_id', mosqueId)
      .eq('status', 'approved')
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) {
      throw error;
    }

    const photos = (data || []) as DatabaseMosquePhoto[];

    const withUrls = await Promise.all(
      photos.map(async (photo) => {
        try {
          const signedUrl = await createSignedPhotoUrl(photo.storage_path);
          return toMosquePhoto(photo, signedUrl);
        } catch {
          return toMosquePhoto(photo, '');
        }
      })
    );

    return withUrls.filter((photo) => Boolean(photo.url));
  };

  const fetchPhotosByStatus = async (
    status: 'pending' | 'approved' | 'rejected',
    page: number = 1,
    itemsPerPage: number = 20
  ) => {
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage - 1;

    const { data, error, count } = await supabase
      .from('mosque_photos')
      .select('*, mosques(name)', { count: 'exact' })
      .eq('status', status)
      .order('created_at', { ascending: false })
      .range(start, end);

    if (error) {
      throw error;
    }

    const withUrls = await Promise.all(
      ((data || []) as Array<DatabaseMosquePhoto & { mosques: { name: string } | null }>).map(async (photo) => {
        try {
          const photoUrl = await createSignedPhotoUrl(photo.storage_path);
          return { ...photo, photo_url: photoUrl };
        } catch {
          return { ...photo, photo_url: '' };
        }
      })
    );

    return {
      data: withUrls.filter((photo) => Boolean(photo.photo_url)),
      count: count || 0,
    };
  };

  const approvePhoto = async (photoId: string) => {
    const { error } = await supabase.rpc('approve_photo', { photo_id: photoId });
    if (error) {
      if (error.code === 'PGRST202') {
        await moderatePhotoFallback(photoId, 'approved', null);
        return;
      }
      throw error;
    }
  };

  const rejectPhoto = async (photoId: string, reason: string) => {
    const { error } = await supabase.rpc('reject_photo', {
      photo_id: photoId,
      reject_reason: reason,
    });
    if (error) {
      if (error.code === 'PGRST202') {
        await moderatePhotoFallback(photoId, 'rejected', reason);
        return;
      }
      throw error;
    }
  };

  const deletePhoto = async (photoId: string, storagePath: string) => {
    const [{ error: dbError }, { error: storageError }] = await Promise.all([
      supabase.from('mosque_photos').delete().eq('id', photoId),
      supabase.storage.from(PHOTO_BUCKET).remove([storagePath]),
    ]);

    if (dbError) {
      throw dbError;
    }

    if (storageError) {
      throw storageError;
    }
  };

  return {
    uploadPhoto,
    uploadPhotos,
    fetchApprovedPhotosForMosque,
    fetchPhotosByStatus,
    approvePhoto,
    rejectPhoto,
    deletePhoto,
    maxPhotoSizeBytes: MAX_PHOTO_SIZE_BYTES,
    allowedImageTypes: Array.from(ALLOWED_IMAGE_TYPES),
  };
};
